import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import { FiChevronLeft } from 'react-icons/fi'
import { getMyDeposits } from 'services/deposits'
import { faPlain, fundName, toman } from 'utilities/helper/format'
import { PulseCard } from 'components/states'
import { withdrawalTrackingPath } from 'pages/fund/withdrawRequest/trackingPath'

const VISIBLE = 3

/**
 * «سرمایهٔ شما در صندوق‌ها» — what the member OWNS.
 *
 * Home answered three questions — what do I owe, whom have I guaranteed, which
 * funds do I manage — and never the fourth. With no answer to «چقدر دارم؟»,
 * money paid into a fund reads as money gone: the member sees it leave their
 * account, sees the instalments they still owe, and nothing that says the
 * capital is still theirs.
 *
 * So the card does two jobs at once, and both are load-bearing:
 *
 * 1. **Contribution.** Their money is not sitting idle in a box; it is out
 *    there being a قرض‌الحسنه loan to somebody having a hard month. The line
 *    counts loans currently being repaid in the funds they hold capital in —
 *    present tense, because "48 loans since you joined" keeps growing after
 *    they withdraw everything and stops being about them.
 * 2. **Reachability.** «هر وقت بخواهید می‌توانید درخواست برداشت بدهید» is
 *    stated on the card, not buried in a menu, because the fear this card
 *    exists to answer is that the money is unreachable.
 *
 * هبه is shown separately and never added to the total. It is a gift: once
 * given it is the fund's money and is not returned on تسویه or on a برداشت.
 * Folding the two together would promise back money that isn't theirs.
 *
 * Hidden entirely when there is nothing to say — no capital, no هبه, no open
 * request — per the home page's rule that empty sections disappear. A member
 * who was added to a fund and never paid in does not have a deposit.
 *
 * The «درخواست برداشت» button only counts funds without an open request. A
 * member may hold one open request per fund, so a fund that already has one is
 * not an answer to «از کدام صندوق؟» — offering it here sent a single-fund
 * member straight past step 1 into four questions that could only end in a 400
 * on the submit button. When no fund is left to ask from, the button is the
 * پیگیری one instead: the honest next step is the request already in flight.
 */
export default function DepositsCard() {
    const navigate = useNavigate()
    const [expanded, setExpanded] = useState(false)
    const { data, isLoading } = useQuery(['my-deposits'], getMyDeposits, {
        staleTime: 60_000,
    })

    // Above the early returns because a hook has to be, and the card is hidden
    // whenever it has nothing to say — so this event is the only measure of how
    // many members the whole سرمایه/برداشت feature is even addressed to.
    useEffect(() => {
        if (!data?.funds?.length) return
        posthog.capture('deposits_card_viewed', {
            funds_count: data.summary.funds_count,
            open_requests_count: data.summary.open_requests_count,
            has_hibah: data.summary.total_hibah > 0,
            supported_loans_count: data.summary.supported_loans_count,
        })
        // Keyed on the fetched data, not on every re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    if (isLoading) return <PulseCard />
    // Silent on error, like ضمانت‌ها: a card that fails to load is not worth an
    // error box on a screen whose primary job is elsewhere.
    if (!data?.funds?.length) return null

    const { summary, funds } = data
    const shown = expanded ? funds : funds.slice(0, VISIBLE)
    const hidden = funds.length - shown.length
    const openRequests = funds.filter((fund) => fund.open_request)
    // the only funds «درخواست برداشت» can actually lead to
    const requestable = funds.filter((fund) => !fund.open_request)

    const openTracking = () => {
        posthog.capture('withdrawal_request_opened', {
            source: 'home',
            blocked: requestable.length === 0,
            open_requests_count: openRequests.length,
        })
        navigate(withdrawalTrackingPath(openRequests))
    }

    return (
        <div className="bg-white border border-divider rounded-card p-4">
            <p className="text-xs text-text-secondary">
                سرمایهٔ شما در صندوق‌ها
            </p>
            <p className="text-[28px] font-bold text-heading leading-tight mt-1">
                {toman(summary.total_capital)}
            </p>
            <p className="text-sm text-text-body mt-[2px]">
                در {faPlain.format(summary.funds_count)} صندوق
            </p>


            {summary.supported_loans_count > 0 && (
                <div className="mt-3 bg-success-2 text-success-on rounded-lg px-3 py-2 text-xs text-right leading-6">
                    این پول با مشارکت دیگر اعضا امروز پشتیبان{' '}
                    {faPlain.format(summary.supported_loans_count)} وام جاری
                    است.
                </div>
            )}

            <div className="mt-3 border-t border-divider pt-1">
                {shown.map((fund) => (
                    <button
                        key={fund.moneypool_id}
                        type="button"
                        onClick={() => {
                            posthog.capture('deposits_fund_opened', {
                                fund_id: fund.moneypool_id,
                            })
                            navigate(`/fund/${fund.moneypool_id}`)
                        }}
                        className="w-full flex items-center justify-between gap-2 py-2 text-right"
                    >
                        <span className="min-w-0">
                            <span className="block text-sm text-text-body line-clamp-2 break-words">
                                {fundName(fund.moneypool_name)}
                            </span>
                            {fund.hibah > 0 && (
                                <span className="block text-[11px] text-text-muted mt-[2px]">
                                    هبهٔ شما: {toman(fund.hibah)}
                                </span>
                            )}
                            {fund.open_request && (
                                <span className="block text-[11px] text-warning-on mt-[2px]">
                                    درخواست برداشت{' '}
                                    {toman(fund.open_request.amount)} — در
                                    انتظار پرداخت
                                </span>
                            )}
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                            <span className="text-sm font-bold text-heading">
                                {toman(fund.capital)}
                            </span>
                            <FiChevronLeft className="text-text-muted shrink-0" />
                        </span>
                    </button>
                ))}
            </div>

            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        posthog.capture('deposits_card_expanded', {
                            hidden_count: hidden,
                        })
                        setExpanded(true)
                    }}
                    className="mt-1 text-xs text-text-secondary"
                >
                    {faPlain.format(hidden)} صندوق دیگر
                </button>
            )}

            {/* The reassurance, said plainly. This is the sentence the card
                exists for: the money did not vanish and they do not have to
                leave the fund to reach it. */}
            <p className="mt-3 text-[11px] text-text-muted leading-6 text-right">
                این پول متعلق به شماست و هر وقت بخواهید می‌توانید بخشی از آن را
                درخواست کنید — بدون خروج از صندوق.
            </p>

            {requestable.length > 0 ? (
                <button
                    type="button"
                    onClick={() => {
                        posthog.capture('withdrawal_request_started', {
                            source: 'home',
                            funds_count: summary.funds_count,
                            requestable_count: requestable.length,
                        })
                        // one fund left to ask from, one possible answer to «از
                        // کدام صندوق؟» — skip straight past a question with a
                        // single option
                        navigate(
                            requestable.length === 1
                                ? `/fund/withdraw-request/${requestable[0].moneypool_id}?step=2`
                                : '/fund/withdraw-request'
                        )
                    }}
                    className="mt-3 w-full rounded-full bg-white border border-green-1 text-green-1 py-2 text-sm font-bold"
                >
                    درخواست برداشت
                </button>
            ) : (
                // Said before the button rather than after a rejected submit.
                // The wording is the backend's own, so the member does not have
                // to work out that two different sentences mean one thing.
                <p className="mt-3 text-[11px] text-warning-on leading-6 text-right">
                    {funds.length === 1
                        ? 'یک درخواست برداشت باز در این صندوق دارید؛ تا بسته شدن آن، درخواست تازه‌ای ثبت نمی‌شود.'
                        : 'در هر صندوقی که سرمایه دارید یک درخواست برداشت باز دارید؛ تا بسته شدن آن‌ها، درخواست تازه‌ای ثبت نمی‌شود.'}
                </p>
            )}

            {openRequests.length > 0 && (
                <button
                    type="button"
                    onClick={openTracking}
                    // when there is nothing left to request, this is the only
                    // thing to do here — so it looks like the button, not like
                    // a footnote under one
                    className={
                        requestable.length > 0
                            ? 'mt-2 w-full text-xs text-green-1'
                            : 'mt-2 w-full rounded-full bg-white border border-green-1 text-green-1 py-2 text-sm font-bold'
                    }
                >
                    پیگیری درخواست‌های برداشت (
                    {faPlain.format(summary.open_requests_count)})
                </button>
            )}
        </div>
    )
}
